import { XCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeferredBookingStatusBadge } from './DeferredBookingStatusBadge';
import type { DeferredBookingInquiry, DeferredBookingDecisionOutcome } from '../types/deferredBookingInquiry';
import { formatDate } from '../utils/formatDate';

interface DeferredBookingDetailPanelProps {
  readonly inquiry: DeferredBookingInquiry;
  readonly isAccepting: boolean;
  readonly isDeclining: boolean;
  readonly onAccept: (id: string) => void;
  readonly onDecline: (id: string, reason: string) => void;
  readonly onClose: () => void;
  readonly lastOutcome: DeferredBookingDecisionOutcome | null;
}

export const DeferredBookingDetailPanel = ({
  inquiry,
  isAccepting,
  isDeclining,
  onAccept,
  onDecline,
  onClose,
  lastOutcome,
}: DeferredBookingDetailPanelProps) => {
  const { t } = useTranslation(['common', 'pages', 'errors']);
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declineError, setDeclineError] = useState('');

  const isAnyPending = isAccepting || isDeclining;
  const isPending = inquiry.status === 'PENDING_OWNER_CONFIRMATION';

  const handleDecline = () => {
    if (declineReason.trim().length === 0) {
      setDeclineError(t('errors:declineReasonRequired'));
      return;
    }
    setDeclineError('');
    onDecline(inquiry.bookingId, declineReason.trim());
    setShowDeclineForm(false);
    setDeclineReason('');
  };

  const handleCancelDecline = () => {
    setShowDeclineForm(false);
    setDeclineReason('');
    setDeclineError('');
  };

  const guestInfo = inquiry.guestInfo;
  const tripDetails = inquiry.tripDetails;

  return (
    <aside
      className="fixed inset-y-0 right-0 w-[480px] bg-white border-l border-gray-200 shadow-xl flex flex-col z-20"
      aria-label={t('pages:deferredBookingDetailsAriaLabel')}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">
          {t('pages:deferredBookingDetailsHeading')}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label={t('pages:closePanel')}
        >
          <XCircle size={20} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {/* Meta */}
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{t('pages:columnBookingId')}</span>
            <span className="font-mono text-xs text-gray-700">{inquiry.bookingId}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{t('pages:columnStatus')}</span>
            <DeferredBookingStatusBadge status={inquiry.status ?? ''} />
          </div>
          {inquiry.ownerReviewExpiresAt && (
            <div className="flex items-center justify-between">
              <span className="text-gray-500">{t('pages:fieldReviewDeadline')}</span>
              <span className="font-medium text-gray-900">
                {formatDate(inquiry.ownerReviewExpiresAt.slice(0, 10))}
              </span>
            </div>
          )}
        </div>

        {/* Stay details */}
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            {t('pages:sectionStayDetails')}
          </p>
          <div className="space-y-1 text-sm">
            {inquiry.checkInDate && (
              <div className="flex items-center justify-between">
                <span className="text-gray-500">{t('pages:columnCheckIn')}</span>
                <span className="text-gray-900">{formatDate(inquiry.checkInDate)}</span>
              </div>
            )}
            {inquiry.checkOutDate && (
              <div className="flex items-center justify-between">
                <span className="text-gray-500">{t('pages:columnCheckOut')}</span>
                <span className="text-gray-900">{formatDate(inquiry.checkOutDate)}</span>
              </div>
            )}
            {inquiry.nights !== null && (
              <div className="flex items-center justify-between">
                <span className="text-gray-500">{t('pages:columnNights')}</span>
                <span className="text-gray-900">{inquiry.nights}</span>
              </div>
            )}
            {inquiry.totalPrice && (
              <div className="flex items-center justify-between">
                <span className="text-gray-500">{t('pages:columnTotal')}</span>
                <span className="font-medium text-gray-900">€{inquiry.totalPrice}</span>
              </div>
            )}
          </div>
        </div>

        {/* Guest info */}
        {guestInfo && (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              {t('pages:sectionGuestInfo')}
            </p>
            <div className="space-y-1 text-sm">
              {(guestInfo.firstName || guestInfo.lastName) && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">{t('pages:fieldName')}</span>
                  <span className="text-gray-900">
                    {guestInfo.firstName} {guestInfo.lastName}
                  </span>
                </div>
              )}
              {guestInfo.email && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">{t('pages:columnSubmitterEmail')}</span>
                  <span className="text-gray-900">{guestInfo.email}</span>
                </div>
              )}
              {guestInfo.phone && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">{t('pages:fieldPhone')}</span>
                  <span className="text-gray-900">{guestInfo.phone}</span>
                </div>
              )}
              {guestInfo.numberOfGuests !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">{t('pages:fieldNumberOfGuests')}</span>
                  <span className="text-gray-900">{guestInfo.numberOfGuests}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Trip details */}
        {tripDetails && (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              {t('pages:sectionTripDetails')}
            </p>
            <div className="space-y-1 text-sm">
              {tripDetails.adultCount !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">{t('pages:fieldAdultCount')}</span>
                  <span className="text-gray-900">{tripDetails.adultCount}</span>
                </div>
              )}
              {tripDetails.childCount !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">{t('pages:fieldChildCount')}</span>
                  <span className="text-gray-900">{tripDetails.childCount}</span>
                </div>
              )}
              {tripDetails.petsIncluded !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">{t('pages:fieldPetsIncluded')}</span>
                  <span className="text-gray-900">
                    {tripDetails.petsIncluded ? t('pages:yes') : t('pages:no')}
                  </span>
                </div>
              )}
              {tripDetails.stayPurpose && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">{t('pages:fieldStayPurpose')}</span>
                  <span className="text-gray-900">{tripDetails.stayPurpose}</span>
                </div>
              )}
              {tripDetails.expectedArrivalWindow && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">{t('pages:fieldArrivalWindow')}</span>
                  <span className="text-gray-900">{tripDetails.expectedArrivalWindow}</span>
                </div>
              )}
              {tripDetails.guestMessage && (
                <div>
                  <span className="text-gray-500 text-xs">{t('pages:fieldGuestMessage')}</span>
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap mt-1">
                    {tripDetails.guestMessage}
                  </p>
                </div>
              )}
              {tripDetails.specialRequests && (
                <div>
                  <span className="text-gray-500 text-xs">{t('pages:fieldSpecialRequests')}</span>
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap mt-1">
                    {tripDetails.specialRequests}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Outcome confirmation */}
        {lastOutcome && (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              {t('pages:sectionOutcome')}
            </p>
            <div className="bg-green-50 border border-green-100 rounded p-3 text-sm text-green-800">
              {lastOutcome.status === 'AWAITING_GUEST_PAYMENT' && (
                <>
                  <p className="font-medium">{t('pages:outcomeAccepted')}</p>
                  {lastOutcome.guestActionExpiresAt && (
                    <p className="text-xs mt-1">
                      {t('pages:guestActionDeadline', {
                        date: formatDate(lastOutcome.guestActionExpiresAt.slice(0, 10)),
                      })}
                    </p>
                  )}
                </>
              )}
              {lastOutcome.status === 'REJECTED' && (
                <p className="font-medium">{t('pages:outcomeDeclined')}</p>
              )}
            </div>
          </div>
        )}

        {/* Decline reason form */}
        {showDeclineForm && (
          <div className="space-y-2">
            <textarea
              value={declineReason}
              onChange={e => {
                setDeclineReason(e.target.value);
                if (declineError) setDeclineError('');
              }}
              placeholder={t('pages:declineReasonPlaceholder')}
              rows={3}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"
              aria-label={t('pages:declineReasonPlaceholder')}
            />
            {declineError && (
              <p className="text-xs text-red-600">{declineError}</p>
            )}
          </div>
        )}
      </div>

      {/* Actions footer */}
      {isPending && !lastOutcome && (
        <div className="px-6 py-4 border-t border-gray-100 space-y-2">
          {!showDeclineForm ? (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => onAccept(inquiry.bookingId)}
                disabled={isAnyPending}
                className="flex-1 text-sm font-medium px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAccepting ? t('pages:accepting') : t('pages:accept')}
              </button>
              <button
                onClick={() => setShowDeclineForm(true)}
                disabled={isAnyPending}
                className="flex-1 text-sm font-medium px-3 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('pages:decline')}
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleDecline}
                disabled={isDeclining}
                className="flex-1 text-sm font-medium px-3 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeclining ? t('pages:declining') : t('pages:confirmDecline')}
              </button>
              <button
                onClick={handleCancelDecline}
                disabled={isDeclining}
                className="text-sm font-medium px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t('pages:cancelReply')}
              </button>
            </div>
          )}
        </div>
      )}
    </aside>
  );
};
